import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import {
  SHOP_PRODUCTS,
  buildCartLineItems,
  formatShopPrice,
  getCartTotal,
  variantKey,
} from './catalog';
import './Shop.css';

const marketingImages = [
  { src: '/shop/marketing/apt300-hats-01.jpg', alt: 'APT 300 hat campaign' },
  { src: '/shop/marketing/apt300-hats-02.jpg', alt: 'APT 300 hat campaign' },
  { src: '/shop/marketing/apt300-hats-03.webp', alt: 'APT 300 hat campaign' },
  { src: '/shop/marketing/apt300-hats-04.webp', alt: 'APT 300 hat campaign' },
];

const cartStorageKey = 'apt300-shop-cart';

function QuantityControl({ value, onChange }) {
  return (
    <div className="shop-quantity-control" aria-label="Quantity">
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} aria-label="Decrease quantity">−</button>
      <span>{value}</span>
      <button type="button" onClick={() => onChange(value + 1)} aria-label="Increase quantity">+</button>
    </div>
  );
}

function CartPanel({ cart, onChangeQuantity, onClose }) {
  const navigate = useNavigate();
  const items = buildCartLineItems(cart);

  const handleCheckout = () => {
    sessionStorage.setItem(cartStorageKey, JSON.stringify(cart));
    navigate('/shop/checkout');
  };

  return (
    <>
      <button type="button" className="shop-cart-backdrop" onClick={onClose} aria-label="Close shopping bag" />
      <aside className="shop-cart-panel" aria-label="Shopping bag">
        <div className="shop-cart-heading">
          <div>
            <p className="shop-kicker">APT 300 SHOP</p>
            <h2>Your bag</h2>
          </div>
          <button type="button" className="shop-icon-button" onClick={onClose} aria-label="Close shopping bag">×</button>
        </div>
        {items.length === 0 ? (
          <p className="shop-empty-copy">Your bag is waiting for a fresh fit.</p>
        ) : (
          <>
            <div className="shop-cart-lines">
              {items.map((item) => (
                <div className="shop-cart-line" key={item.variantId}>
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.variantName}</span>
                    <span>{formatShopPrice(item.unitAmount)} each</span>
                  </div>
                  <QuantityControl
                    value={item.quantity}
                    onChange={(quantity) => onChangeQuantity(item.variantId, quantity)}
                  />
                </div>
              ))}
            </div>
            <div className="shop-cart-total"><span>Subtotal</span><strong>{formatShopPrice(getCartTotal(cart))}</strong></div>
            <button type="button" className="shop-primary-button shop-full-button" onClick={handleCheckout}>Continue to checkout</button>
            <p className="shop-small-copy">Secure payment by Stripe. Shipping details are collected at checkout.</p>
          </>
        )}
      </aside>
    </>
  );
}

function ProductCard({ product, selectedVariantId, onSelectVariant, onAdd, inventoryCount }) {
  const selectedVariant = product.variants.find((variant) => variant.id === selectedVariantId) || product.variants[0];
  const image = selectedVariant.image || product.images[0];

  return (
    <article className="shop-product-card">
      <div className="shop-product-image-wrap">
        <img src={image} alt={`${product.name} ${selectedVariant.name}`} className="shop-product-image" />
      </div>
      <div className="shop-product-content">
        <div className="shop-product-title-row">
          <h2>{product.name}</h2>
          <strong>{formatShopPrice(product.priceCents)}</strong>
        </div>
        <dl className="shop-product-details">
          {product.details.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl>
        <div className="shop-variant-block">
          <span className="shop-label">{product.variants.length > 1 ? 'Choose your colour' : 'Available style'}</span>
          <div className="shop-variant-list">
            {product.variants.map((variant) => (
              <button
                type="button"
                key={variant.id}
                className={`shop-variant-button${selectedVariantId === variant.id ? ' is-selected' : ''}`}
                onClick={() => onSelectVariant(variant.id)}
              >
                {variant.name}
              </button>
            ))}
          </div>
        </div>
        <div className="shop-product-footer">
          <button type="button" className="shop-primary-button" onClick={() => onAdd(product.id, selectedVariant.id)} disabled={inventoryCount === 0}>Add to bag</button>
        </div>
      </div>
    </article>
  );
}

export default function Shop() {
  const location = useLocation();
  const [slide, setSlide] = useState(0);
  const [cart, setCart] = useState({});
  const [selectedVariants, setSelectedVariants] = useState(() => Object.fromEntries(SHOP_PRODUCTS.map((product) => [product.id, product.variants[0].id])));
  const [inventory, setInventory] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const timer = window.setInterval(() => setSlide((current) => (current + 1) % marketingImages.length), 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/storefront/shop-products`)
      .then((response) => (response.ok ? response.json() : []))
      .then((products) => setInventory(Object.fromEntries(products.map((product) => [product.id, product.inventoryCount]))))
      .catch(() => undefined);
  }, []);

  const bagCount = useMemo(() => Object.values(cart).reduce((sum, quantity) => sum + quantity, 0), [cart]);

  const addToBag = (productId, variantId) => {
    const key = variantKey(productId, variantId);
    setCart((current) => ({ ...current, [key]: (current[key] || 0) + 1 }));
    setAnnouncement('Added to your bag.');
    setCartOpen(true);
  };

  const changeQuantity = (key, quantity) => {
    setCart((current) => {
      const next = { ...current };
      if (quantity <= 0) delete next[key];
      else next[key] = quantity;
      return next;
    });
  };

  return (
    <div className="shop-page">
      <Header />
      <main>
        <section className="shop-hero" aria-labelledby="shop-title">
          <div className="shop-hero-copy">
            <h1 id="shop-title">TROUBLE BROTHERS</h1>
            <a href="#shop-products" className="shop-outline-button">Shop the drop <span>↓</span></a>
          </div>
          <div className="shop-hero-carousel" aria-label="APT 300 hat campaign">
            {marketingImages.map((image, index) => <img key={image.src} src={image.src} alt={image.alt} className={`shop-hero-slide shop-hero-slide-${index + 1}${index === slide ? ' is-active' : ''}`} />)}
            <div className="shop-carousel-controls">
              <button type="button" onClick={() => setSlide((slide - 1 + marketingImages.length) % marketingImages.length)} aria-label="Previous campaign image">←</button>
              <div>{marketingImages.map((image, index) => <button type="button" key={image.src} className={index === slide ? 'is-active' : ''} onClick={() => setSlide(index)} aria-label={`Show campaign image ${index + 1}`} />)}</div>
              <button type="button" onClick={() => setSlide((slide + 1) % marketingImages.length)} aria-label="Next campaign image">→</button>
            </div>
          </div>
        </section>

        <section id="shop-products" className="shop-products-section">
          <div className="shop-section-heading"><span aria-hidden="true" /><button type="button" className="shop-bag-button" onClick={() => setCartOpen(true)}>Bag <span>{bagCount}</span></button></div>
          {location.search.includes('success=1') && <p className="shop-success-banner" role="status">Order received — thanks for supporting APT 300. Your payment receipt is on its way.</p>}
          {announcement && <p className="shop-announcement" role="status">{announcement}</p>}
          <div className="shop-product-grid">
            {SHOP_PRODUCTS.map((product) => <ProductCard key={product.id} product={product} selectedVariantId={selectedVariants[product.id]} onSelectVariant={(variantId) => setSelectedVariants((current) => ({ ...current, [product.id]: variantId }))} onAdd={addToBag} inventoryCount={inventory[product.id]} />)}
          </div>
        </section>
      </main>
      {cartOpen && <CartPanel cart={cart} onChangeQuantity={changeQuantity} onClose={() => setCartOpen(false)} />}
    </div>
  );
}
